import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  MailOutlined,
  PictureOutlined,
  UploadOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Switch, Upload, notification } from 'antd';
import AlertBox from '@/components/alert-box';
import {
  ChipToggle,
  Group,
  SettingSwitch,
} from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { uploadSingleFile } from '@/services/files.service';
import { getFieldState } from '@/utils/formUtils';
import { QUERY_KEY } from '@/shared/constants';
import { TIME_SHEET_DOCS_SETTINGS_COMPANY_GRAPH_QUERY } from '@/shared/time-sheet';
import EmailTemplatesSection from '@/components/email-templates/EmailTemplatesSection';
import CustomTemplatesForm from './CustomTemplatesForm';

const initialValues = {
  // Document creation
  holidayLeave: true,
  paidLeave: true,
  nonPaidLeave: true,
  bonus: false,
  suspension: false,
  maternityLeave: true,
  pregnancyLeave: true,
  additionalHolidayDays: true,
  // Email sending
  holidayLeaveEmail: true,
  paidLeaveEmail: true,
  nonPaidLeaveEmail: true,
  bonusEmail: false,
  suspensionEmail: false,
  maternityLeaveEmail: true,
  pregnancyLeaveEmail: true,
  additionalHolidayDaysEmail: true,
  workFromHomeEmail: true,
  // Employee emails
  sendRequestEmailsToEmployees: true,
  // Signature
  showSignatureOnDocs: false,
  hideEmployeeSignature: false,
  // Memorandum
  showMemorandumInDocs: false,
};

// Column width constant for alignment
const COLUMN_WIDTH = 'w-[90px]';

// Row component with two toggles: Document and Email
function DocsSettingsRow({ title, docName, emailName, disabled }) {
  const { t } = useTranslation(['forms']);

  return (
    <div className="flex select-none items-center justify-between gap-4">
      <p className="flex-1 font-semibold">{title}</p>
      <div className="flex items-center">
        {/* Document toggle */}
        <div className={`flex items-center justify-center ${COLUMN_WIDTH}`}>
          <FileTextOutlined
            className="mr-2 text-gray-500"
            title={t('createDocument', { ns: 'forms' })}
          />
          <Form.Item
            label={null}
            className="!mb-0"
            name={docName}
            valuePropName="checked"
          >
            <Switch size="small" disabled={disabled} />
          </Form.Item>
        </div>
        {/* Email toggle */}
        <div className={`flex items-center justify-center ${COLUMN_WIDTH}`}>
          <MailOutlined
            className="mr-2 text-gray-500"
            title={t('sendEmail', { ns: 'forms' })}
          />
          <Form.Item
            label={null}
            className="!mb-0"
            name={emailName}
            valuePropName="checked"
          >
            <Switch size="small" disabled={disabled} />
          </Form.Item>
        </div>
      </div>
    </div>
  );
}

export default function DocsSettingsForm({
  isEdited,
  className,
  onClose,
  companyId,
  // Company profile: the same fields and save, laid out as setting cards.
  cards = false,
}) {
  const { t } = useTranslation(['forms', 'messages', 'menus', 'translations']);

  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [memorandumFile, setMemorandumFile] = useState(null);
  const [existingMemorandum, setExistingMemorandum] = useState(null);

  const { data: companyData } = useQuery({
    queryKey: [QUERY_KEY.company, companyId, 'docsSettings'],
    queryFn: async () =>
      await API.companies.getCompanyById(
        companyId,
        TIME_SHEET_DOCS_SETTINGS_COMPANY_GRAPH_QUERY
      ),
    enabled: !!companyId,
    retry: false,
  });

  useEffect(() => {
    if (companyData?.companyMemorandum?.location) {
      setExistingMemorandum(companyData.companyMemorandum);
    }
  }, [companyData]);

  useEffect(() => {
    if (companyData?.companyDocsSettings) {
      const {
        holidayLeave,
        paidLeave,
        nonPaidLeave,
        bonus,
        suspension,
        maternityLeave,
        pregnancyLeave,
        additionalHolidayDays,
        holidayLeaveEmail,
        paidLeaveEmail,
        nonPaidLeaveEmail,
        bonusEmail,
        suspensionEmail,
        maternityLeaveEmail,
        pregnancyLeaveEmail,
        additionalHolidayDaysEmail,
        sendRequestEmailsToEmployees,
        showSignatureOnDocs,
        hideEmployeeSignature,
        showMemorandumInDocs,
      } = companyData.companyDocsSettings;

      form.setFieldsValue({
        // Document creation
        holidayLeave: holidayLeave ?? true,
        paidLeave: paidLeave ?? true,
        nonPaidLeave: nonPaidLeave ?? true,
        bonus: bonus ?? false,
        suspension: suspension ?? false,
        maternityLeave: maternityLeave ?? true,
        pregnancyLeave: pregnancyLeave ?? true,
        additionalHolidayDays: additionalHolidayDays ?? true,
        // Email sending
        holidayLeaveEmail: holidayLeaveEmail ?? true,
        paidLeaveEmail: paidLeaveEmail ?? true,
        nonPaidLeaveEmail: nonPaidLeaveEmail ?? true,
        bonusEmail: bonusEmail ?? false,
        suspensionEmail: suspensionEmail ?? false,
        maternityLeaveEmail: maternityLeaveEmail ?? true,
        pregnancyLeaveEmail: pregnancyLeaveEmail ?? true,
        additionalHolidayDaysEmail: additionalHolidayDaysEmail ?? true,
        workFromHomeEmail:
          companyData.companyDocsSettings.workFromHomeEmail ?? true,
        // Employee emails
        sendRequestEmailsToEmployees: sendRequestEmailsToEmployees ?? true,
        // Signature
        showSignatureOnDocs: showSignatureOnDocs ?? false,
        hideEmployeeSignature: hideEmployeeSignature ?? false,
        // Memorandum
        showMemorandumInDocs: showMemorandumInDocs ?? false,
      });
    } else {
      form.setFieldsValue(initialValues);
    }
  }, [companyData, form]);

  // Backend-driven field visibility: the company response carries a per-role
  // permission map (COMPANY_GENERAL_DATA_FORM_FIELDS). The signature /
  // memorandum / report-email / custom-template settings are gated there, so
  // we never hardcode role checks here — we just reflect the backend decision.
  const formFields = companyData?.COMPANY_GENERAL_DATA_FORM_FIELDS;
  const canSeeDocsField = (field) =>
    getFieldState({
      formFields,
      formKeys: ['companyDocsSettings', field],
      isEdited: true,
    }).visible;

  const handleReset = () => {
    form.resetFields();
    onClose();
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      return await API.companies.updateCompanyById(companyId, payload);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries([
        QUERY_KEY.company,
        companyId,
        'docsSettings',
      ]);
      setMemorandumFile(null);
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
        placement: 'bottomRight',
      });
      onClose();
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.message,
        placement: 'bottomRight',
      });
    },
  });

  const onFinish = async (values) => {
    const formValues = { ...values };

    // Preserve any custom-template entries that were set via the dedicated
    // upload/delete endpoints — this form only edits toggles/flags, but the
    // backend's findOneAndUpdate replaces the whole subdocument, so we must
    // echo customTemplates back to avoid wiping them.
    const existingCustomTemplates =
      companyData?.companyDocsSettings?.customTemplates;

    const payload = {
      companyDocsSettings: {
        ...formValues,
        ...(existingCustomTemplates
          ? { customTemplates: existingCustomTemplates }
          : {}),
      },
    };

    // Upload memorandum file if new one was selected
    if (memorandumFile) {
      try {
        const uploadResult = await uploadSingleFile(memorandumFile);
        if (uploadResult?.data) {
          payload.companyMemorandum = {
            location: uploadResult.data.location,
            typeOfFile: uploadResult.data.mimetype,
            originalName: uploadResult.data.originalName,
          };
        }
      } catch {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: 'Failed to upload memorandum image',
          placement: 'bottomRight',
        });
        return;
      }
    }

    await mutateAsync(payload);
  };

  const memorandumUpload = (
    <div className="mb-2 mt-2">
      {existingMemorandum?.originalName && !memorandumFile && (
        <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
          <FileTextOutlined />
          <span>{existingMemorandum.originalName}</span>
          {isEdited && (
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setExistingMemorandum(null);
                setMemorandumFile(null);
              }}
            />
          )}
        </div>
      )}
      {isEdited && (
        <Upload
          accept="image/*"
          maxCount={1}
          beforeUpload={(file) => {
            setMemorandumFile(file);
            return false;
          }}
          onRemove={() => setMemorandumFile(null)}
          fileList={memorandumFile ? [memorandumFile] : []}
        >
          <Button size="small" icon={<UploadOutlined />}>
            {existingMemorandum?.originalName
              ? t('changeMemorandum', {
                  ns: 'forms',
                  defaultValue: 'Promeni memorandum',
                })
              : t('uploadMemorandum', {
                  ns: 'forms',
                  defaultValue: 'Upload memorandum',
                })}
          </Button>
        </Upload>
      )}
    </div>
  );

  const footer = isEdited && (
    <div className="form-footer form-footer--sticky">
      <div className="flex gap-2">
        <Button
          className="btn-default btn-default--hover"
          onClick={handleReset}
        >
          {t('reset', { ns: 'forms' })}
        </Button>

        <Button
          type="primary"
          htmlType="submit"
          loading={isPending}
          disabled={isPending}
          className="btn-primary"
        >
          {t('save', { ns: 'forms' })}
        </Button>
      </div>
    </div>
  );

  if (cards) {
    const ui = (key) =>
      t(`companyProfile.cardsUi.${key}`, { ns: 'translations' });
    const docTypes = [
      ['holidayLeave', t('holidayLeave', { ns: 'forms' })],
      ['paidLeave', t('paidLeave', { ns: 'forms' })],
      ['nonPaidLeave', t('nonPaidLeave', { ns: 'forms' })],
      ['bonus', t('bonus', { ns: 'forms' })],
      ['suspension', t('suspension', { ns: 'forms' })],
      ['maternityLeave', t('maternityLeave', { ns: 'forms' })],
      ['pregnancyLeave', t('pregnancyLeave', { ns: 'forms' })],
      ['additionalHolidayDays', t('additionalHolidayDays', { ns: 'messages' })],
    ];
    const chip = (name, icon, label) => (
      <Form.Item name={name} noStyle>
        <ChipToggle icon={icon} label={label} disabled={!isEdited} />
      </Form.Item>
    );
    const docCard = (title, docName, emailName) => (
      <div
        key={emailName}
        className="flex flex-col gap-2 rounded-xl border border-zinc-200 bg-white p-2.5"
      >
        <span className="text-[12.5px] font-bold text-zinc-900">{title}</span>
        <span className="flex flex-wrap gap-1.5">
          {docName &&
            chip(docName, <FileTextOutlined />, t('document', { ns: 'forms' }))}
          {chip(emailName, <MailOutlined />, t('email', { ns: 'forms' }))}
        </span>
      </div>
    );
    const toggle = (name, icon, title, children) =>
      canSeeDocsField(name) && (
        <Form.Item name={name} noStyle>
          <SettingSwitch
            locked={!isEdited}
            icon={icon}
            title={title}
            wide={!!children}
          >
            {children}
          </SettingSwitch>
        </Form.Item>
      );

    return (
      <Form
        form={form}
        name="time-sheet_docs_settings_form"
        initialValues={initialValues}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className={cn('form grid gap-5 p-4', className)}
      >
        <section>
          <h3 className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
            {ui('groupDocMail')}
          </h3>
          <p className="mb-2 text-[12px] text-zinc-500">
            {t('timesheetHrDocsDefinitionInfo', { ns: 'messages' })}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {docTypes.map(([name, title]) =>
              docCard(title, name, `${name}Email`)
            )}
            {docCard(
              t('workFromHome', { ns: 'forms' }),
              null,
              'workFromHomeEmail'
            )}
          </div>
        </section>

        <Group title={ui('groupLook')}>
          {toggle(
            'showMemorandumInDocs',
            <PictureOutlined />,
            t('showMemorandumInDocs', { ns: 'forms' }),
            <div className="pl-[42px]">{memorandumUpload}</div>
          )}
          {toggle(
            'showSignatureOnDocs',
            <EditOutlined />,
            t('showSignatureOnDocs', { ns: 'forms' })
          )}
          {toggle(
            'hideEmployeeSignature',
            <UserDeleteOutlined />,
            t('hideEmployeeSignature', { ns: 'forms' })
          )}
          {toggle(
            'sendRequestEmailsToEmployees',
            <MailOutlined />,
            t('sendRequestEmailsToEmployees', { ns: 'forms' })
          )}
        </Group>

        {canSeeDocsField('customTemplates') && (
          <CustomTemplatesForm
            isEdited={isEdited}
            companyId={companyId}
            customTemplates={companyData?.companyDocsSettings?.customTemplates}
          />
        )}

        {canSeeDocsField('customTemplates') && (
          <EmailTemplatesSection companyId={companyId} isEdited={isEdited} />
        )}

        {footer}
      </Form>
    );
  }

  return (
    <Form
      form={form}
      name="time-sheet_docs_settings_form"
      initialValues={initialValues}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={cn('form p-4', className)}
    >
      {/* These are intro/instruction notes — they should scroll away with the
          content, not stay pinned (the .alert-box base class is sticky). */}
      <AlertBox variant="info" className="!static">
        <span>
          {t('timesheetHrDocsDefinitionInfo', {
            ns: 'messages',
          })}
        </span>
      </AlertBox>

      <AlertBox variant="warning" className="!static">
        <span>
          {t('timesheetHrDocsTypesInstruction', {
            ns: 'messages',
          })}
        </span>
      </AlertBox>

      {/* Column headers */}
      <div className="mb-2 mt-5 flex items-center justify-between gap-4 text-sm text-gray-500">
        <span className="flex-1">{t('leaveType', { ns: 'forms' })}</span>
        <div className="flex items-center">
          <div
            className={`flex items-center justify-center gap-1 ${COLUMN_WIDTH}`}
          >
            <FileTextOutlined />
            <span>{t('document', { ns: 'forms' })}</span>
          </div>
          <div
            className={`flex items-center justify-center gap-1 ${COLUMN_WIDTH}`}
          >
            <MailOutlined />
            <span>{t('email', { ns: 'forms' })}</span>
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-3">
        <DocsSettingsRow
          title={t('holidayLeave', { ns: 'forms' })}
          docName="holidayLeave"
          emailName="holidayLeaveEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('paidLeave', { ns: 'forms' })}
          docName="paidLeave"
          emailName="paidLeaveEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('nonPaidLeave', { ns: 'forms' })}
          docName="nonPaidLeave"
          emailName="nonPaidLeaveEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('bonus', { ns: 'forms' })}
          docName="bonus"
          emailName="bonusEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('suspension', { ns: 'forms' })}
          docName="suspension"
          emailName="suspensionEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('maternityLeave', { ns: 'forms' })}
          docName="maternityLeave"
          emailName="maternityLeaveEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('pregnancyLeave', { ns: 'forms' })}
          docName="pregnancyLeave"
          emailName="pregnancyLeaveEmail"
          disabled={!isEdited}
        />

        <hr />

        <DocsSettingsRow
          title={t('additionalHolidayDays', { ns: 'messages' })}
          docName="additionalHolidayDays"
          emailName="additionalHolidayDaysEmail"
          disabled={!isEdited}
        />

        <hr />

        {/* WFH - email only, no document generation */}
        <div className="flex select-none items-center justify-between gap-4">
          <p className="flex-1 font-semibold">
            {t('workFromHome', { ns: 'forms' })}
          </p>
          <div className="flex items-center">
            <div className={COLUMN_WIDTH} />
            <div className={`flex items-center justify-center ${COLUMN_WIDTH}`}>
              <MailOutlined
                className="mr-2 text-gray-500"
                title={t('sendEmail', { ns: 'forms' })}
              />
              <Form.Item
                label={null}
                className="!mb-0"
                name="workFromHomeEmail"
                valuePropName="checked"
              >
                <Switch size="small" disabled={!isEdited} />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Signature toggle - single switch, aligned with Document column */}
        {canSeeDocsField('showSignatureOnDocs') && (
          <>
            <hr />
            <div className="flex select-none items-center justify-between gap-4">
              <p className="flex-1 font-semibold">
                {t('showSignatureOnDocs', { ns: 'forms' })}
              </p>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center ${COLUMN_WIDTH}`}
                >
                  <Form.Item
                    label={null}
                    className="!mb-0"
                    name="showSignatureOnDocs"
                    valuePropName="checked"
                  >
                    <Switch size="small" disabled={!isEdited} />
                  </Form.Item>
                </div>
                <div className={COLUMN_WIDTH} />
              </div>
            </div>
          </>
        )}

        {/* Hide employee signature toggle */}
        {canSeeDocsField('hideEmployeeSignature') && (
          <>
            <hr />
            <div className="flex select-none items-center justify-between gap-4">
              <p className="flex-1 font-semibold">
                {t('hideEmployeeSignature', { ns: 'forms' })}
              </p>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center ${COLUMN_WIDTH}`}
                >
                  <Form.Item
                    label={null}
                    className="!mb-0"
                    name="hideEmployeeSignature"
                    valuePropName="checked"
                  >
                    <Switch size="small" disabled={!isEdited} />
                  </Form.Item>
                </div>
                <div className={COLUMN_WIDTH} />
              </div>
            </div>
          </>
        )}

        {/* Send request emails to employees toggle */}
        {canSeeDocsField('sendRequestEmailsToEmployees') && (
          <>
            <hr />
            <div className="flex select-none items-center justify-between gap-4">
              <p className="flex-1 font-semibold">
                {t('sendRequestEmailsToEmployees', { ns: 'forms' })}
              </p>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center ${COLUMN_WIDTH}`}
                >
                  <Form.Item
                    label={null}
                    className="!mb-0"
                    name="sendRequestEmailsToEmployees"
                    valuePropName="checked"
                  >
                    <Switch size="small" disabled={!isEdited} />
                  </Form.Item>
                </div>
                <div className={COLUMN_WIDTH} />
              </div>
            </div>
          </>
        )}

        {/* Memorandum toggle + upload */}
        {canSeeDocsField('showMemorandumInDocs') && (
          <>
            <hr />
            <div className="flex select-none items-center justify-between gap-4">
              <p className="flex-1 font-semibold">
                {t('showMemorandumInDocs', { ns: 'forms' })}
              </p>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center ${COLUMN_WIDTH}`}
                >
                  <Form.Item
                    label={null}
                    className="!mb-0"
                    name="showMemorandumInDocs"
                    valuePropName="checked"
                  >
                    <Switch size="small" disabled={!isEdited} />
                  </Form.Item>
                </div>
                <div className={COLUMN_WIDTH} />
              </div>
            </div>

            {/* Memorandum upload */}
            {memorandumUpload}
          </>
        )}
      </div>

      {canSeeDocsField('customTemplates') && (
        <CustomTemplatesForm
          isEdited={isEdited}
          companyId={companyId}
          customTemplates={companyData?.companyDocsSettings?.customTemplates}
        />
      )}

      {canSeeDocsField('customTemplates') && (
        <div className="mt-6">
          <EmailTemplatesSection companyId={companyId} isEdited={isEdited} />
        </div>
      )}

      {isEdited && (
        <div className="form-footer form-footer--sticky">
          <div className="flex gap-2">
            <Button
              loading={false}
              disabled={false}
              className="btn-default btn-default--hover"
              onClick={handleReset}
            >
              {t('reset', { ns: 'forms' })}
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              disabled={isPending}
              className="btn-primary"
            >
              {t('save', { ns: 'forms' })}
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
}
